// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// rules.ts — the whole night, as a pure integer state machine. No DOM, no clock, no Math.random.
//
// THE ONE SENTENCE: a turn is five slots, perception is evaluated at the end of EVERY slot, and the
// slots you do not spend are the only thing that cools the building down.
//
// Evaluating per slot rather than per turn is what makes the SHAPE of a route the skill instead of its
// endpoint. Walking four cells through the corner of an arc and out the other side is a different act
// from walking four cells around it, and a per-turn check cannot tell them apart — it would let a
// player teleport through the one cell the whole plan was about.
//
// WHAT RAISES HEAT, EXHAUSTIVELY: being perceived, a lens catching you, a minder READING a mark you
// left, taking a case, and forcing a door. THAT IS ALL. In particular a din costs nothing. The design
// review found the original table charged heat for almost every verb, including up to sixteen per
// slug out of three slugs — which made the one tool sold to the player as "sound is a tool, not only a
// mistake" a fine instead. A thrown slug is now genuinely free and genuinely loud, and the summons it
// buys is a play. Everything expensive is something the building learned about you.

import {
  BAND_FLOOR,
  BOLT_BONUS,
  BOLT_CELLS,
  Band,
  CARRY_DIN,
  CASE_READ_RANGE,
  CELLS,
  CHASE_EXTRA_STEP,
  CREEP_PENALTY,
  CUSTODIAN_SPEED,
  DECAY_BASE,
  DECAY_CAP,
  DECAY_GATE,
  DECAY_PER_SLOT,
  FLOOR_DIN,
  Floor,
  GHOST_PCT,
  GLOOM_PENALTY,
  GRABS_TO_LOSE,
  HEAT_FORCE,
  HEAT_GRAB,
  HEAT_LENS,
  HEAT_MAX,
  HEAT_PERCEIVED,
  HEAT_READ,
  TIME_CAP,
  TIME_PCT,
  LENS_PERIOD,
  LENS_RANGE,
  MINDER_STEPS,
  MIN_THROW,
  Mark,
  NO_DECAY_IN_DUCT,
  QUIET_PCT,
  READS_PER_TURN,
  SCUFF_FOLLOW,
  SCUFF_LIFE,
  SHOUT_DIN,
  SLOTS,
  SLUGS_CARRIED,
  SLUG_RANGE,
  SOUND_DUCT,
  TORCH_CELLS,
  VERB_DIN,
  VERB_SLOTS,
  Verb,
  BASE_RANGE,
} from './tuning';
import {
  DIRS,
  DoorState,
  chebyshev,
  canStep,
  facingOf,
  hasLos,
  inArc,
  inBounds,
  idx,
  neighbours,
  soundField,
  stepToward,
  walkField,
  xOf,
  yOf,
  type Door,
  type Grid,
} from './map';
import type { Mode } from './modes';
import { orderFor, patrolStep, sweepZones } from './director';

export enum MState {
  Patrol = 0,
  Investigate = 1,
  Chase = 2,
}

export interface Minder {
  id: number;
  cell: number;
  facing: number;
  state: MState;
  /** Investigate/chase destination, or -1. */
  target: number;
  loop: number[];
  loopPos: number;
  /** SWEEP only: which of the three zones this body is pinned to. */
  zone: number;
  /**
   * True once this minder has stood orthogonally adjacent and chasing through the end of a turn.
   * The player always gets exactly one full turn of warning before a grab, and mechanism invariant
   * GRAB IS WARNED asserts it at zero tolerance — without it, a mutation that grabs on contact passes
   * every other invariant in the file.
   */
  warned: boolean;
  reads: number;
  reinforcement: boolean;
}

export interface Lens {
  id: number;
  cell: number;
  /** Facing at turn 0; it rotates one octant per turn on LENS_PERIOD. Lenses are DEAF. */
  facing0: number;
}

export interface Item {
  id: number;
  cell: number;
  value: number;
  taken: boolean;
  banked: boolean;
  /** Removed from the world by the custodian — this is score the building deleted. */
  confiscated: boolean;
}

export interface MarkRec {
  kind: Mark;
  cell: number;
  /** Scuff only: the octant the intruder bolted along. */
  dir: number;
  born: number;
}

export interface Building {
  grid: Grid;
  doors: Door[];
  entry: number;
  exits: number[];
  chutes: number[];
  ducts: [number, number];
  /** One breaker cell per room; -1 for a room without one. */
  breakers: number[];
  items: Item[];
  /**
   * `facing` is optional and exists for the hand-authored tutorial floor: a body with a one-cell loop
   * has no direction to derive from, so it defaults to due east — which quietly pointed the tutorial's
   * one minder away from the thing it was supposed to be watching.
   */
  minderStarts: { cell: number; loop: number[]; facing?: number }[];
  lensStarts: { cell: number; facing0: number }[];
  strongroom: number;
  /** The three anchors SWEEP splits the floor from. */
  anchors: number[];
}

export type Ev = Record<string, number | string | boolean | number[]>;

export interface State {
  mode: Mode;
  seed: number;
  b: Building;
  grid: Grid;
  doors: Door[];
  turn: number;
  slot: number;
  pos: number;
  inDuct: boolean;
  /** True once the intruder has stood anywhere other than the entry cell — see checkExit. */
  leftEntry: boolean;
  /**
   * The last cell anybody actually SAW the intruder in, or the entry until somebody has.
   *
   * Reinforcements walk at this, not at `pos`. Handing a body that has just come through the front
   * door your exact live position is omniscience, and on Hot Floor — which opens at LOCKDOWN — it
   * produced a permanent homing escort from turn three that made the mode unwinnable by anybody.
   */
  lkp: number;
  /** Verb.Step, Verb.Creep or Verb.Bolt — a sticky posture, not a per-action modifier. */
  posture: Verb;
  heat: number;
  /** The ratchet: heat may never fall below this again. */
  floorHeat: number;
  bandTouched: Band;
  minders: Minder[];
  lenses: Lens[];
  items: Item[];
  roomGloom: Uint8Array;
  marks: MarkRec[];
  slugs: number;
  carrying: number[];
  banked: number[];
  /** hotfloor: the dossier, once taken. */
  hasObjective: boolean;
  grabs: number;
  perceivedEver: boolean;
  sweepTouched: boolean;
  /** LOCKDOWN: the body that walks your loot back to the strongroom. He has NO arc. */
  custodian: number;
  custodianItem: number;
  reinforceCount: number;
  /** SWEEP grid-search bookkeeping, reset whenever the band changes. */
  searched: Uint8Array;
  over: boolean;
  outcome: 'escaped' | 'caught' | 'timeout' | null;
  recording: boolean;
  events: Ev[];
  /** Test-only mutation switches. Every one must turn the mechanism audit RED — see mechanism.test.ts. */
  mut: Readonly<Record<string, boolean>>;
}

export interface Action {
  verb: Verb;
  arg: number;
}

const NB: number[] = [];

// ── construction ────────────────────────────────────────────────────────────

export function createState(
  b: Building,
  mode: Mode,
  seed: number,
  opts: { recording?: boolean; mut?: Record<string, boolean> } = {},
): State {
  const doors = b.doors.map((d) => ({ ...d }));
  const s: State = {
    mode,
    seed,
    b,
    grid: b.grid,
    doors,
    turn: 0,
    slot: 0,
    pos: b.entry,
    inDuct: false,
    leftEntry: false,
    lkp: b.entry,
    posture: Verb.Step,
    heat: mode.startHeat,
    floorHeat: 0,
    bandTouched: Band.Calm,
    minders: b.minderStarts.slice(0, mode.minders).map((m, i) => ({
      id: i,
      cell: m.cell,
      facing: m.facing ?? (m.loop.length > 1 ? facingOf(m.cell, m.loop[0]) : 0),
      state: MState.Patrol,
      target: -1,
      loop: m.loop,
      loopPos: 0,
      zone: i % 3,
      warned: false,
      reads: 0,
      reinforcement: false,
    })),
    lenses: b.lensStarts.slice(0, mode.lenses).map((l, i) => ({ id: i, cell: l.cell, facing0: l.facing0 })),
    items: b.items.map((it) => ({ ...it })),
    roomGloom: new Uint8Array(b.grid.roomCount),
    marks: [],
    slugs: SLUGS_CARRIED,
    carrying: [],
    banked: [],
    hasObjective: false,
    grabs: 0,
    perceivedEver: false,
    sweepTouched: false,
    custodian: -1,
    custodianItem: -1,
    reinforceCount: 0,
    searched: new Uint8Array(CELLS),
    over: false,
    outcome: null,
    recording: opts.recording ?? false,
    events: [],
    mut: opts.mut ?? {},
  };
  // A mode that STARTS at a band cannot be talked out of it. The ratchet normally stops one band short
  // so escalation stays reversible, but Hot Floor opens mid-search by definition — and with only the
  // ordinary rule it cooled out of SWEEP after a single quiet turn, which deleted the entire premise
  // of the mode on turn one. Whatever heat you began with is a floor you never get back under.
  s.floorHeat = mode.startHeat;
  // hotfloor opens already locked down, which is the ordinary ratchet doing its ordinary job rather
  // than a special case: the band is derived from the starting heat and the floor follows.
  applyBand(s, true);
  return s;
}

const ev = (s: State, e: Ev): void => {
  if (s.recording) s.events.push(e);
};

/**
 * Every order a body is given goes on the record, including the ones the director issues quietly.
 *
 * SWEEP's zone search and RESTLESS's mark-chasing both set a target directly, and neither used to say
 * so — which left runs where the whole building was searching but the stream contained no evidence
 * that anybody had ever gone to look at anything. The liveness invariant read that, correctly, as a
 * simulation that had stopped.
 */
export function logOrder(s: State, id: number, verb: string, target: number, cause: string): void {
  ev(s, { t: 'ORDER', turn: s.turn, id, verb, target, cause });
}

// ── light, posture, perception ──────────────────────────────────────────────

/**
 * Is this cell lit? The floor ramp on screen draws exactly this and nothing else, which is why the
 * player can read `effRange` as a shape instead of a formula.
 */
export function isBright(s: State, cell: number): boolean {
  if (s.mut.LIGHT_STUCK_BRIGHT) return true;
  if (!s.mode.mainsOut) {
    const r = s.grid.room[cell];
    return r < 0 || !s.roomGloom[r];
  }
  // Mains out: the building is gloom everywhere, and the only light on the floor is carried by the
  // people looking for you.
  if (!s.mode.torches) return false;
  for (const m of s.minders) {
    if (chebyshev(m.cell, cell) > TORCH_CELLS) continue;
    if (!inArc(m.cell, m.facing, cell, false)) continue;
    if (hasLos(s.grid, s.doors, m.cell, cell)) return true;
  }
  return false;
}

export const bandOf = (heat: number): Band => {
  let b = Band.Calm;
  for (let i = 1; i < BAND_FLOOR.length; i++) if (heat >= BAND_FLOOR[i]) b = i as Band;
  return b;
};
export const band = (s: State): Band => bandOf(s.heat);

/** The posture penalty, derived from the sticky posture alone. */
export function postureDelta(posture: Verb, mut: Readonly<Record<string, boolean>>): number {
  if (mut.POSTURE_FLAT) return 0;
  if (posture === Verb.Creep) return -CREEP_PENALTY;
  if (posture === Verb.Bolt) return BOLT_BONUS;
  return 0;
}

/** effRange = max(1, base - gloom - posture). Integers throughout; see tuning.ts for why subtractive. */
export function effRange(s: State, at: number): number {
  if (s.mut.ARC_RANGE) return 0;
  const base = BASE_RANGE[band(s)];
  const gloom = isBright(s, at) ? 0 : GLOOM_PENALTY;
  return Math.max(1, base - gloom + postureDelta(s.posture, s.mut));
}

/** Does minder `m` perceive the intruder right now? Angle, then range, then line of sight. */
export function seesIntruder(s: State, m: Minder): boolean {
  if (s.inDuct) return false;
  const r = effRange(s, s.pos);
  if (r <= 0) return false;
  if (chebyshev(m.cell, s.pos) > r) return false;
  if (!inArc(m.cell, m.facing, s.pos, band(s) >= Band.Sweep)) return false;
  return hasLos(s.grid, s.doors, m.cell, s.pos);
}

export const lensFacing = (l: Lens, turn: number): number => (l.facing0 + turn) % LENS_PERIOD & 7;

export function lensSees(s: State, l: Lens): boolean {
  if (s.inDuct) return false;
  if (chebyshev(l.cell, s.pos) > LENS_RANGE) return false;
  if (!inArc(l.cell, lensFacing(l, s.turn), s.pos, true)) return false;
  return hasLos(s.grid, s.doors, l.cell, s.pos);
}

// ── heat ────────────────────────────────────────────────────────────────────

function addHeat(s: State, delta: number, reason: string): void {
  if (delta === 0) return;
  s.heat = Math.max(s.floorHeat, Math.min(HEAT_MAX, s.heat + delta));
  ev(s, { t: 'HEAT', turn: s.turn, delta, reason });
  applyBand(s, false);
}

/** Recompute the band, run the ratchet, and let the building act on the change. */
function applyBand(s: State, initial: boolean): void {
  const now = bandOf(s.heat);
  if (now > s.bandTouched) {
    const from = s.bandTouched;
    s.bandTouched = now;
    // THE RATCHET STOPS ONE BAND SHORT, and it has to. Pinning the floor at the band you just touched
    // makes bands strictly one-way: once SWEEP is reached, heat can never fall below fifty, so it can
    // never leave SWEEP, and "you can talk the building back down" — the design's central promise, and
    // the thing that makes being seen a play rather than a failure — was false by construction. The sim
    // measured it precisely: reversibility 0.00, in every mode, under every policy. One band below
    // makes both halves true at once. The building remembers, and it can still be calmed by one step.
    if (!s.mut.RATCHET_OFF) s.floorHeat = Math.max(s.mode.startHeat, BAND_FLOOR[Math.max(0, now - 1)]);
    s.searched.fill(0);
    if (now >= Band.Sweep) {
      s.sweepTouched = true;
      lockInteriorDoors(s);
      assignZones(s);
    }
    if (!initial) ev(s, { t: 'BAND', turn: s.turn, from, to: now });
    else ev(s, { t: 'BAND', turn: s.turn, from, to: now });
  } else if (now < Band.Sweep && s.bandTouched >= Band.Sweep) {
    // Cannot happen while the ratchet holds, and is here so that a RATCHET_OFF mutation produces a
    // building that visibly unlocks rather than one that silently keeps its doors shut.
    releaseAutoLocked(s);
  }
  if (s.heat < s.floorHeat) s.heat = s.floorHeat;
}

function lockInteriorDoors(s: State): void {
  for (const d of s.doors) {
    if (d.state === DoorState.Locked) continue;
    d.state = DoorState.Locked;
    d.autoLocked = true;
    ev(s, { t: 'DOOR', turn: s.turn, cell: d.cell, state: DoorState.Locked });
  }
}

function releaseAutoLocked(s: State): void {
  for (const d of s.doors) {
    if (!d.autoLocked) continue;
    d.autoLocked = false;
    d.state = DoorState.Closed;
    ev(s, { t: 'DOOR', turn: s.turn, cell: d.cell, state: DoorState.Closed });
  }
}

function assignZones(s: State): void {
  const zones = sweepZones(s.grid, s.doors, s.b.anchors);
  for (const m of s.minders) m.zone = zones[m.cell] >= 0 ? zones[m.cell] : m.id % 3;
}

// ── din ─────────────────────────────────────────────────────────────────────

/** Every extra case you are carrying adds a cell of noise to every step. Greed is audible. */
export const carryDin = (s: State): number => Math.max(0, s.carrying.length - 1) * CARRY_DIN;

function isMove(v: Verb): boolean {
  return v === Verb.Step || v === Verb.Creep || v === Verb.Bolt;
}

export function dinOf(s: State, v: Verb, at: number): number {
  let loud = VERB_DIN[v];
  if (loud === 0 && !isMove(v)) return 0;
  if (isMove(v)) {
    if (v === Verb.Creep) return 0; // creeping is silent whatever you are standing on
    loud += FLOOR_DIN[s.grid.floorT[at]] + carryDin(s);
  }
  return loud + (loud > 0 ? s.mode.dinBonus : 0);
}

/**
 * Raise a din at `at` and summon EVERY ear inside it — not the nearest, all of them.
 *
 * That is what makes a slug a tool rather than a mistake: a clatter thrown into the east wing empties
 * the east wing, because every minder that can hear it comes, and each one abandons whatever it was
 * doing. Mechanism invariant NO DEAF EAR asserts the summoned set is EXACTLY the audible set, in both
 * directions, over the whole corpus.
 */
export function raiseDin(s: State, at: number, loud: number, cause: string): void {
  if (loud <= 0) return;
  if (s.mut.DIN_RADIUS) loud = 0;
  const extra = s.inDuct && at === s.pos ? SOUND_DUCT : 0;
  const field = soundField(s.grid, s.doors, at, loud, extra);
  const heard: number[] = [];
  for (const m of s.minders) {
    if (field[m.cell] < 0) continue;
    heard.push(m.id);
    if (m.state === MState.Chase) continue; // a chase already outranks a clatter
    m.state = MState.Investigate;
    m.target = at;
    ev(s, { t: 'ORDER', turn: s.turn, id: m.id, verb: 'investigate', target: at, cause });
  }
  ev(s, { t: 'DIN', turn: s.turn, slot: s.slot, cell: at, loud, heard });
}

// ── marks ───────────────────────────────────────────────────────────────────

function writeMark(s: State, kind: Mark, cell: number, dir = 0): void {
  if (s.mut.MARKS_NEVER_WRITTEN) return;
  // One live mark of a kind per cell; a repeat refreshes rather than stacking, so a corridor you use
  // twice is not twice as incriminating.
  const existing = s.marks.findIndex((m) => m.kind === kind && m.cell === cell);
  if (existing >= 0) s.marks.splice(existing, 1);
  s.marks.push({ kind, cell, dir, born: s.turn });
  ev(s, { t: 'MARK', turn: s.turn, kind, cell, dir });
}

const markLive = (s: State, m: MarkRec): boolean =>
  m.kind !== Mark.Scuff || s.turn - m.born < SCUFF_LIFE;

function expireMarks(s: State): void {
  s.marks = s.marks.filter((m) => markLive(s, m));
}

/**
 * A minder reads at most one live mark per turn, and READING CONSUMES IT.
 *
 * The design made MISSING CASE permanent and re-readable every turn, so after a second grab the heat
 * climbed on its own no matter what the player did — the dial the entire game is about was a timer
 * with extra steps. Consuming every mark once, ever, is what makes your own past something you can
 * outrun rather than a sentence.
 */
function readMarks(s: State): void {
  if (s.mut.READS_NEVER_FIRE) return;
  for (const m of s.minders) {
    if (m.reads >= READS_PER_TURN) continue;
    let best = -1;
    let bestKey = 1 << 30;
    for (let i = 0; i < s.marks.length; i++) {
      const k = s.marks[i];
      if (!markLive(s, k)) continue;
      const d = chebyshev(m.cell, k.cell);
      if (k.kind === Mark.MissingCase) {
        if (d > CASE_READ_RANGE) continue;
        if (!inArc(m.cell, m.facing, k.cell, band(s) >= Band.Sweep)) continue;
        if (!hasLos(s.grid, s.doors, m.cell, k.cell)) continue;
      } else {
        // On the cell, or orthogonally adjacent to it.
        const md = Math.abs(xOf(m.cell) - xOf(k.cell)) + Math.abs(yOf(m.cell) - yOf(k.cell));
        if (md > 1) continue;
      }
      // Deterministic and total: lowest cell index, then kind order. Two minders reading the same turn
      // must resolve identically on every device.
      const key = k.cell * 8 + k.kind;
      if (key < bestKey) {
        bestKey = key;
        best = i;
      }
    }
    if (best < 0) continue;
    const k = s.marks[best];
    s.marks.splice(best, 1);
    m.reads++;
    ev(s, { t: 'READ', turn: s.turn, id: m.id, kind: k.kind, cell: k.cell });
    addHeat(s, HEAT_READ, 'read');
    reactToMark(s, m, k);
  }
}

function reactToMark(s: State, m: Minder, k: MarkRec): void {
  let target = k.cell;
  let verb = 'investigate';
  switch (k.kind) {
    case Mark.Scuff: {
      // Follow it. The whole point of a scuff is that it points where you went.
      const [dx, dy] = DIRS[k.dir & 7];
      let c = k.cell;
      for (let i = 0; i < SCUFF_FOLLOW; i++) {
        const nx = xOf(c) + dx;
        const ny = yOf(c) + dy;
        if (nx < 0 || ny < 0) break;
        const n = idx(nx, ny);
        if (n < 0 || n >= CELLS || !canStep(s.grid, s.doors, n)) break;
        c = n;
      }
      target = c;
      break;
    }
    case Mark.OpenDoor: {
      const d = s.grid.doorAt[k.cell];
      if (d >= 0 && s.doors[d].state === DoorState.Open) {
        s.doors[d].state = DoorState.Closed;
        ev(s, { t: 'DOOR', turn: s.turn, cell: k.cell, state: DoorState.Closed });
      }
      break;
    }
    case Mark.GloomRoom: {
      // He walks to the breaker and puts the lights back on. This is the loop that produces most of
      // the situations nobody enumerated: light changes effRange, effRange changes perception,
      // perception changes heat, heat changes how thoroughly the next mark gets read.
      const room = s.grid.room[k.cell];
      if (room >= 0 && s.roomGloom[room]) {
        s.roomGloom[room] = 0;
        ev(s, { t: 'BREAK', turn: s.turn, room, gloom: 0 });
      }
      break;
    }
    case Mark.MissingCase:
    case Mark.ForcedDoor: {
      verb = 'chase';
      m.state = MState.Chase;
      m.target = k.cell;
      ev(s, { t: 'ORDER', turn: s.turn, id: m.id, verb, target: m.target, cause: 'read' });
      return;
    }
    case Mark.Slug:
      break;
  }
  if (m.state !== MState.Chase) {
    m.state = MState.Investigate;
    m.target = target;
    ev(s, { t: 'ORDER', turn: s.turn, id: m.id, verb, target, cause: 'read' });
  }
}

// ── legality ────────────────────────────────────────────────────────────────

const orthoAdjacent = (a: number, b: number): boolean =>
  Math.abs(xOf(a) - xOf(b)) + Math.abs(yOf(a) - yOf(b)) === 1;

/** Where a bolt from `from` along `dir` lands, or -1 if the straight line is blocked. */
export function boltTarget(s: State, from: number, dir: number): number {
  const [dx, dy] = DIRS[dir & 7];
  if (dx !== 0 && dy !== 0) return -1; // movement is 4-adjacent
  let c = from;
  for (let i = 0; i < BOLT_CELLS; i++) {
    const nx = xOf(c) + dx;
    const ny = yOf(c) + dy;
    if (!inBounds(nx, ny)) return -1;
    const n = idx(nx, ny);
    if (!canStep(s.grid, s.doors, n)) return -1;
    c = n;
  }
  return c;
}

/**
 * Is this action legal right now, with `spent` slots already staged this turn?
 *
 * `spent` is a parameter rather than state because staging is a UI concern and the turn machine is
 * not: the player builds a list, the machine executes one. Keeping the budget out of `State` means
 * there is no half-staged turn a replay could land in.
 */
export function canDo(s: State, a: Action, spent = 0): boolean {
  if (s.over) return false;
  if (spent + VERB_SLOTS[a.verb] > SLOTS) return false;
  const g = s.grid;
  switch (a.verb) {
    case Verb.Hold:
      return true;
    case Verb.Step:
    case Verb.Creep:
      if (s.inDuct) return false;
      return orthoAdjacent(s.pos, a.arg) && canStep(g, s.doors, a.arg);
    case Verb.Bolt: {
      if (s.inDuct) return false;
      for (const o of [0, 2, 4, 6]) if (boltTarget(s, s.pos, o) === a.arg) return true;
      return false;
    }
    case Verb.OpenDoor: {
      if (s.inDuct) return false;
      const d = g.doorAt[a.arg];
      if (d < 0 || !orthoAdjacent(s.pos, a.arg)) return false;
      if (s.doors[d].state === DoorState.Open) return false;
      // A keycard opens a locked door as cheaply and quietly as an unlocked one.
      return s.doors[d].state === DoorState.Closed || s.mode.keycard;
    }
    case Verb.CloseDoor: {
      if (s.inDuct) return false;
      const d = g.doorAt[a.arg];
      return d >= 0 && orthoAdjacent(s.pos, a.arg) && s.doors[d].state === DoorState.Open;
    }
    case Verb.ForceDoor: {
      if (s.inDuct || s.mode.keycard) return false;
      const d = g.doorAt[a.arg];
      return d >= 0 && orthoAdjacent(s.pos, a.arg) && s.doors[d].state === DoorState.Locked;
    }
    case Verb.Breaker: {
      if (s.inDuct || s.mode.mainsOut) return false;
      const room = g.room[a.arg];
      return (
        room >= 0 && s.b.breakers[room] === a.arg && orthoAdjacent(s.pos, a.arg) && !s.roomGloom[room]
      );
    }
    case Verb.Grab: {
      if (s.inDuct) return false;
      const it = s.items.find((i) => i.cell === a.arg && !i.taken && !i.confiscated);
      return !!it && (s.pos === a.arg || orthoAdjacent(s.pos, a.arg));
    }
    case Verb.Bank:
      if (s.inDuct) return false;
      return s.b.chutes.includes(a.arg) && s.pos === a.arg && s.carrying.length > 0;
    case Verb.Slug: {
      if (s.inDuct || s.slugs <= 0) return false;
      const d = chebyshev(s.pos, a.arg);
      // MIN_THROW is a real rule in the real action table, shared by every mode. The tutorial's
      // refusal "throw it away from you, not at your feet" is therefore the game's rule and can be
      // asserted against the real rules — a step that invents its own legality is not driving the
      // state machine at all.
      if (d < MIN_THROW || d > SLUG_RANGE) return false;
      return canStep(g, s.doors, a.arg) && hasLos(g, s.doors, s.pos, a.arg);
    }
    case Verb.Duct: {
      if (s.inDuct) return true; // climbing out
      return g.ductAt[s.pos] >= 0;
    }
    default:
      return false;
  }
}

// ── applying one action ─────────────────────────────────────────────────────

function apply(s: State, a: Action): void {
  const from = s.pos;
  switch (a.verb) {
    case Verb.Hold:
      break;
    case Verb.Step:
    case Verb.Creep:
      s.pos = a.arg;
      break;
    case Verb.Bolt:
      s.pos = a.arg;
      writeMark(s, Mark.Scuff, from, facingOf(from, a.arg));
      break;
    case Verb.OpenDoor: {
      const d = s.grid.doorAt[a.arg];
      s.doors[d].state = DoorState.Open;
      s.doors[d].autoLocked = false;
      ev(s, { t: 'DOOR', turn: s.turn, cell: a.arg, state: DoorState.Open });
      writeMark(s, Mark.OpenDoor, a.arg);
      break;
    }
    case Verb.CloseDoor: {
      const d = s.grid.doorAt[a.arg];
      s.doors[d].state = DoorState.Closed;
      ev(s, { t: 'DOOR', turn: s.turn, cell: a.arg, state: DoorState.Closed });
      // Closing it again removes the evidence. Tidying up is a real, cheap, quiet play.
      const i = s.marks.findIndex((m) => m.kind === Mark.OpenDoor && m.cell === a.arg);
      if (i >= 0) s.marks.splice(i, 1);
      break;
    }
    case Verb.ForceDoor: {
      const d = s.grid.doorAt[a.arg];
      s.doors[d].state = DoorState.Open;
      s.doors[d].autoLocked = false;
      ev(s, { t: 'DOOR', turn: s.turn, cell: a.arg, state: DoorState.Open });
      writeMark(s, Mark.ForcedDoor, a.arg);
      addHeat(s, HEAT_FORCE, 'force');
      break;
    }
    case Verb.Breaker: {
      const room = s.grid.room[a.arg];
      s.roomGloom[room] = 1;
      ev(s, { t: 'BREAK', turn: s.turn, room, gloom: 1 });
      writeMark(s, Mark.GloomRoom, a.arg);
      break;
    }
    case Verb.Grab: {
      const it = s.items.find((i) => i.cell === a.arg && !i.taken && !i.confiscated)!;
      it.taken = true;
      s.carrying.push(it.id);
      if (it.value === s.mode.objectiveValue && s.mode.loot === 0) s.hasObjective = true;
      writeMark(s, Mark.MissingCase, a.arg);
      addHeat(s, HEAT_GRAB, 'grab');
      ev(s, { t: 'TAKE', turn: s.turn, cell: a.arg, value: it.value });
      break;
    }
    case Verb.Bank: {
      for (const id of s.carrying) {
        s.items[id].banked = true;
        s.banked.push(id);
      }
      ev(s, { t: 'BANK', turn: s.turn, cell: a.arg, count: s.carrying.length });
      s.carrying = [];
      break;
    }
    case Verb.Slug:
      s.slugs--;
      writeMark(s, Mark.Slug, a.arg);
      break;
    case Verb.Duct:
      if (s.inDuct) {
        s.inDuct = false;
        // You come out of the other mouth. That is the whole topology the floor gets in place of stairs.
        s.pos = s.b.ducts[0] === s.pos ? s.b.ducts[1] : s.b.ducts[0];
      } else {
        s.inDuct = true;
      }
      break;
  }
  const loud = dinOf(s, a.verb, a.verb === Verb.Slug ? a.arg : from);
  if (loud > 0) raiseDin(s, a.verb === Verb.Slug ? a.arg : s.pos, loud, 'act');
}

// ── perception, once per slot ───────────────────────────────────────────────

function evaluatePerception(s: State): void {
  if (s.over) return;
  const r = effRange(s, s.pos);
  for (const m of s.minders) {
    if (!seesIntruder(s, m)) continue;
    ev(s, { t: 'PERCEIVE', turn: s.turn, slot: s.slot, by: m.id, kind: 'minder', cell: s.pos, effRange: r });
    s.perceivedEver = true;
    s.lkp = s.pos;
    m.state = MState.Chase;
    m.target = s.pos;
    ev(s, { t: 'ORDER', turn: s.turn, id: m.id, verb: 'chase', target: s.pos, cause: 'perceive' });
    addHeat(s, HEAT_PERCEIVED, 'perceived');
    // He shouts, and a shout is the loudest thing in the building — and it is about you.
    raiseDin(s, s.pos, SHOUT_DIN, 'shout');
  }
  for (const l of s.lenses) {
    if (!lensSees(s, l)) continue;
    ev(s, { t: 'PERCEIVE', turn: s.turn, slot: s.slot, by: l.id, kind: 'lens', cell: s.pos, effRange: LENS_RANGE });
    s.perceivedEver = true;
    addHeat(s, HEAT_LENS, 'lens');
  }
}

function stepMinders(s: State): void {
  for (const m of s.minders) {
    let to = -1;
    if (m.state === MState.Patrol) to = patrolStep(s, m);
    else if (m.target >= 0 && m.target !== m.cell) to = stepToward(s.grid, s.doors, m.cell, m.target);
    // A body never walks THROUGH you. It stops in the doorway and takes hold of you next turn, which
    // is what the one-turn warning is made of — stepping onto the intruder's own cell left the two of
    // them co-located, `orthoAdjacent` false, and the grab could never fire at all.
    if (to === s.pos && !s.inDuct) to = -1;
    if (to < 0 || to === m.cell) {
      if (m.state === MState.Investigate && m.cell === m.target) {
        // Arrived and found nothing: sweep the room with your eyes, then go back to work.
        m.facing = (m.facing + 2) & 7;
        m.state = MState.Patrol;
        m.target = -1;
      } else if (m.state === MState.Chase && m.cell === m.target) {
        m.state = MState.Investigate;
      }
      continue;
    }
    m.facing = facingOf(m.cell, to);
    m.cell = to;
    s.searched[to] = 1;
  }
}

function emitBodies(s: State): void {
  if (!s.recording) return;
  for (const m of s.minders) {
    s.events.push({ t: 'MINDER', turn: s.turn, slot: s.slot, id: m.id, cell: m.cell, facing: m.facing, state: m.state });
  }
  s.events.push({
    t: 'POSE',
    turn: s.turn,
    slot: s.slot,
    cell: s.pos,
    // The verb and the floor, NEVER the light level and never a posture factor. The design's POSE
    // carried `bright`, so the auditor consumed the game's own light value and re-derived everything
    // except the thing most likely to be wrong — a tautology sitting exactly where the design said the
    // load was. audit.ts maintains its own light mask from the BREAK stream and its own posture from
    // this verb, and two mutation controls (LIGHT_STUCK_BRIGHT, POSTURE_FLAT) prove it can tell.
    verb: s.posture,
    floor: s.grid.floorT[s.pos],
    inDuct: s.inDuct,
    carry: s.carrying.length,
  });
}

// ── the turn ────────────────────────────────────────────────────────────────

/** Lay staged actions into the five slots; a k-slot action resolves on its LAST slot. */
export function timelineOf(actions: readonly Action[]): (Action | null)[] {
  const t: (Action | null)[] = new Array(SLOTS).fill(null);
  let i = 0;
  for (const a of actions) {
    const n = VERB_SLOTS[a.verb];
    if (n === 0) continue;
    if (i + n > SLOTS) break;
    i += n;
    t[i - 1] = a;
  }
  return t;
}

export const slotsOf = (actions: readonly Action[]): number =>
  actions.reduce((n, a) => n + VERB_SLOTS[a.verb], 0);

export function commitTurn(s: State, actions: readonly Action[]): void {
  if (s.over) return;
  const spent = slotsOf(actions);
  const line = timelineOf(actions);
  for (const m of s.minders) m.reads = 0;

  for (let slot = 0; slot < SLOTS; slot++) {
    s.slot = slot;
    const a = line[slot];
    if (a) apply(s, a);
    if (s.pos !== s.b.entry) s.leftEntry = true;
    const chasing = s.minders.some((m) => m.state === MState.Chase);
    if ((MINDER_STEPS as readonly number[]).includes(slot) || (chasing && slot === CHASE_EXTRA_STEP)) {
      stepMinders(s);
    }
    // BODIES FIRST, THEN PERCEPTION. The order matters to nothing inside the simulation and everything
    // to the audit: an observer reading the stream has to know where everybody is standing BEFORE it is
    // told who can see whom, or it checks every claim against last slot's positions.
    emitBodies(s);
    evaluatePerception(s);
    ev(s, { t: 'TICK', turn: s.turn, slot, heat: s.heat, band: band(s) });
    if (checkExit(s)) return;
  }

  endOfTurn(s, SLOTS - spent);
}

/**
 * You cannot leave through a door you have not gone in by.
 *
 * The front door IS the entry cell, so without this the very first turn of every run was: take the
 * case beside you, and you are already standing on an exit holding loot — run over, one turn, ninety
 * per cent "escape" rate. The balance sim reported that as a healthy game for three passes, because
 * every aggregate it measures looks fine when the answer is "the player left immediately": no
 * escalation, no tier separation, and all three blindness controls identical, since no mechanic was
 * ever reached. `leftEntry` is the whole fix — you have to actually go into the building.
 */
function checkExit(s: State): boolean {
  if (s.inDuct || !s.leftEntry) return false;
  if (!validExits(s).includes(s.pos)) return false;
  // Walking out with it in your hands counts. The chute is insurance and silence, not the only till.
  for (const id of s.carrying) {
    s.items[id].banked = true;
    s.banked.push(id);
  }
  s.carrying = [];
  if (s.banked.length === 0 && !s.hasObjective) return false;
  finish(s, 'escaped');
  return true;
}

function endOfTurn(s: State, unspent: number): void {
  // The band is reconciled every turn, not only when heat happens to be ADDED. Deriving it solely
  // inside addHeat means a building that arrives at a band any other way — a mode that starts there,
  // a decay that crosses a boundary — keeps acting like the band it used to be in, with its doors
  // unlocked and its zones unassigned.
  applyBand(s, false);
  readMarks(s);
  runDirector(s);
  runCustodian(s);
  applyGrabs(s);
  if (s.over) return;
  coolDown(s, unspent);
  expireMarks(s);
  reinforce(s);
  s.turn++;
  if (s.turn >= s.mode.turns) finish(s, 'timeout');
}

/**
 * Unspent slots cool the building — but only while nobody is close, and never from inside a duct.
 *
 * Both conditions are corrections. The design gated on distance 5, which on an 80-cell floor is almost
 * never satisfied, so the game's advertised central decision would not have been a decision on most
 * turns; DECAY_GATE is a named constant and tests/balance.test.ts sweeps it. And a duct was an
 * un-perceivable, un-hearable, freely-cooling cell, so six turns of sitting in one erased a full
 * LOCKDOWN and the REVERSIBILITY assertion would have gone green through a strategy nobody designed —
 * which is why there is a DUCT-CAMPER control arm looking for exactly that.
 */
function coolDown(s: State, unspent: number): void {
  if (s.mut.DECAY_OFF) return;
  if (NO_DECAY_IN_DUCT && s.inDuct) return;
  if (s.minders.some((m) => m.state === MState.Chase)) return;
  const field = soundField(s.grid, s.doors, s.pos, DECAY_GATE);
  const near = s.minders.some((m) => field[m.cell] >= 0);
  if (near) return;
  const amount = Math.min(DECAY_CAP, DECAY_BASE + DECAY_PER_SLOT * Math.max(0, unspent));
  const before = s.heat;
  s.heat = Math.max(s.floorHeat, s.heat - amount);
  if (s.heat !== before) ev(s, { t: 'HEAT', turn: s.turn, delta: s.heat - before, reason: 'quiet' });
}

function runDirector(s: State): void {
  for (const m of s.minders) orderFor(s, m);
}

/**
 * LOCKDOWN's custodian: he walks your uncollected loot back to the strongroom, two cells a turn.
 *
 * He has NO arc and never perceives anybody — mechanism invariant CUSTODIAN HAS NO ARC asserts it —
 * and he is drawn deliberately off the threat ramp, because the player has to read "not a threat"
 * instantly. He is the losing state made into something you stand and watch: every turn he is walking
 * is a live decision about whether to cut and run.
 */
function runCustodian(s: State): void {
  if (band(s) < Band.Lockdown) return;
  // He tidies away LOOT. In a mode whose whole objective is one dossier there is nothing for him to
  // tidy that is not the game itself: he walked off with the only thing worth taking, and every run
  // that reached LOCKDOWN became unwinnable from that moment on while still politely running to the
  // clock. Deleting the objective is not a difficulty curve, it is a cancelled match.
  if (s.mode.loot === 0) return;
  if (s.custodian < 0) s.custodian = s.b.strongroom;

  // Empty-handed: walk to the nearest case still lying on the floor. Deterministic tiebreak on the
  // lower cell index, because two equally close cases must resolve the same way on every device.
  if (s.custodianItem < 0) {
    const field = walkField(s.grid, s.doors, s.custodian);
    let best = -1;
    let bestD = 1 << 20;
    for (const it of s.items) {
      if (it.taken || it.confiscated || it.cell < 0) continue;
      const d = field[it.cell];
      if (d < 0) continue;
      if (d < bestD || (d === bestD && it.cell < s.items[best].cell)) {
        bestD = d;
        best = it.id;
      }
    }
    if (best < 0) return;
    s.custodianItem = best;
  }

  const item = s.items[s.custodianItem];
  // A case the player took out from under him is no longer his errand.
  if (item.taken || item.confiscated) {
    s.custodianItem = -1;
    return;
  }
  const holding = item.cell < 0;
  const goal = holding ? s.b.strongroom : item.cell;
  for (let i = 0; i < CUSTODIAN_SPEED && s.custodian !== goal; i++) {
    const to = stepToward(s.grid, s.doors, s.custodian, goal);
    if (to < 0) break;
    s.custodian = to;
  }
  if (!holding && s.custodian === item.cell) {
    item.cell = -1; // lifted off the board; the player can no longer reach it
    ev(s, { t: 'CUSTODIAN', turn: s.turn, cell: s.custodian, carrying: item.id, delivered: -1 });
    return;
  }
  if (holding && s.custodian === s.b.strongroom) {
    item.confiscated = true;
    item.cell = s.b.strongroom;
    s.custodianItem = -1;
    ev(s, { t: 'CUSTODIAN', turn: s.turn, cell: s.custodian, carrying: -1, delivered: item.id });
    return;
  }
  ev(s, { t: 'CUSTODIAN', turn: s.turn, cell: s.custodian, carrying: holding ? item.id : -1, delivered: -1 });
}

function applyGrabs(s: State): void {
  for (const m of s.minders) {
    const touching = m.state === MState.Chase && orthoAdjacent(m.cell, s.pos) && !s.inDuct;
    if (!touching) {
      m.warned = false;
      continue;
    }
    if (!m.warned && !s.mut.GRAB_INSTANT) {
      // One full turn of warning, always. This is the game's core fairness promise.
      m.warned = true;
      continue;
    }
    s.grabs++;
    ev(s, { t: 'GRAB', turn: s.turn, id: m.id, ordinal: s.grabs });
    // He takes what you were holding and puts you back at the door.
    for (const id of s.carrying) {
      s.items[id].taken = false;
      s.items[id].cell = s.b.strongroom;
    }
    s.carrying = [];
    s.pos = s.b.entry;
    m.warned = false;
    m.state = MState.Investigate;
    if (s.grabs >= GRABS_TO_LOSE) {
      finish(s, 'caught');
      return;
    }
  }
}

function reinforce(s: State): void {
  if (band(s) < Band.Lockdown) return;
  if (s.turn % 4 !== 3) return;
  if (s.minders.length >= 5) return;
  s.reinforceCount++;
  const id = s.minders.length;
  // They come in at the FRONT, which drains bodies toward the entrance and empties the back — at the
  // cost of sealing the exit you came in by. Pushing the building to LOCKDOWN is a play, not only a
  // punishment.
  s.minders.push({
    id,
    cell: s.b.entry,
    facing: 6,
    state: MState.Chase,
    target: s.lkp,
    loop: [s.b.entry],
    loopPos: 0,
    zone: id % 3,
    warned: false,
    reads: 0,
    reinforcement: true,
  });
  ev(s, { t: 'ORDER', turn: s.turn, id, verb: 'chase', target: s.lkp, cause: 'reinforce' });
}

// ── scoring and ending ──────────────────────────────────────────────────────

export function bankedValue(s: State): number {
  let v = 0;
  for (const id of s.banked) v += s.items[id].value;
  return v;
}

/**
 * score = banked * (1 + ghost + quiet) + 2 per unused turn, and zero if you bank nothing.
 *
 * MULTIPLICATIVE, and this was the single worst finding in the design review. The flat version paid
 * GHOST 40 + QUIET 25 = 65 for never being seen and never touching SWEEP — more than the three most
 * valuable cases in the building combined — so the score-maximising line was a timid two-case CALM run
 * and the entire four-band escalation ladder became a scoring catastrophe to be avoided. It paid the
 * player not to play the game. Multiplying makes greed and stealth multiply rather than compete.
 */
export function scoreOf(s: State): number {
  const v = bankedValue(s);
  if (v === 0) return 0;
  const left = Math.max(0, s.mode.turns - s.turn);
  const mult =
    100 +
    (s.perceivedEver ? 0 : GHOST_PCT) +
    (s.sweepTouched ? 0 : QUIET_PCT) +
    Math.min(TIME_CAP, left * TIME_PCT);
  return Math.floor((v * mult) / 100);
}

function finish(s: State, outcome: 'escaped' | 'caught' | 'timeout'): void {
  if (s.over) return;
  s.over = true;
  s.outcome = outcome;
  ev(s, { t: 'END', turn: s.turn, outcome, score: scoreOf(s) });
}

// ── read-only helpers the UI, the bot and the verifier all share ────────────

/**
 * The exits that would actually work RIGHT NOW.
 *
 * One function, used by the rules, the bot, the renderer and the summary alike — because on Hot Floor,
 * where only the roof duct counts, the bot was routing to the front door and standing on it. From the
 * outside that looked exactly like a thief who could not find the objective, and it cost ninety per
 * cent of that mode's runs. An exit the game will not honour must not be an exit anything else
 * believes in.
 */
export function validExits(s: State): number[] {
  return s.b.exits.filter((e, i) => {
    if (e < 0) return false;
    if (s.mode.ductExitOnly && s.grid.ductAt[e] < 0) return false;
    // LOCKDOWN seals the front door. It is exit 0, and it is the one you came in by.
    return !(i === 0 && band(s) >= Band.Lockdown);
  });
}

/**
 * Every cell an intruder standing in it would be perceived from, at this posture.
 *
 * `ahead` projects each body one step along whatever it is currently doing and each lens one octant —
 * which is precisely the DASHED next-turn arc the board draws. Both masks exist so that the renderer,
 * the bot and the route preview all read the same picture: the bot is not allowed to know anything a
 * player looking at the screen does not, and it is not asked to plan on less either. Scoring routes
 * against present positions alone made it walk into a cone that was visibly about to sweep onto it.
 */
export function threatMask(s: State, posture: Verb = s.posture, ahead = false, ignoreGloom = false): Uint8Array {
  const out = new Uint8Array(CELLS);
  const wide = band(s) >= Band.Sweep;
  const base = BASE_RANGE[band(s)];
  const eyes = s.minders.map((m) => {
    if (!ahead) return { cell: m.cell, facing: m.facing };
    const to =
      m.state === MState.Patrol
        ? patrolStep(s, { ...m, loopPos: m.loopPos })
        : m.target >= 0 && m.target !== m.cell
          ? stepToward(s.grid, s.doors, m.cell, m.target)
          : -1;
    return to >= 0 ? { cell: to, facing: facingOf(m.cell, to) } : { cell: m.cell, facing: m.facing };
  });
  for (let c = 0; c < CELLS; c++) {
    if (!canStep(s.grid, s.doors, c)) continue;
    const gloom = ignoreGloom || isBright(s, c) ? 0 : GLOOM_PENALTY;
    const r = Math.max(1, base - gloom + postureDelta(posture, s.mut));
    for (const e of eyes) {
      if (chebyshev(e.cell, c) > r) continue;
      if (!inArc(e.cell, e.facing, c, wide)) continue;
      if (!hasLos(s.grid, s.doors, e.cell, c)) continue;
      out[c] = 1;
      break;
    }
    if (out[c]) continue;
    for (const l of s.lenses) {
      if (chebyshev(l.cell, c) > LENS_RANGE) continue;
      if (!inArc(l.cell, lensFacing(l, s.turn + (ahead ? 1 : 0)), c, true)) continue;
      if (!hasLos(s.grid, s.doors, l.cell, c)) continue;
      out[c] = 1;
      break;
    }
  }
  return out;
}

/** The cells reachable this turn with the slots remaining, as a walking-distance field. */
export function reachable(s: State): Int16Array {
  return walkField(s.grid, s.doors, s.pos);
}

/** Every object verb legal from where the intruder is standing, for the action picker and the bot. */
export function adjacentInteractions(s: State, spent = 0): Action[] {
  const out: Action[] = [];
  neighbours(s.pos, NB);
  const cells = [s.pos, ...NB];
  for (const c of cells) {
    for (const v of [Verb.OpenDoor, Verb.CloseDoor, Verb.ForceDoor, Verb.Breaker, Verb.Grab, Verb.Bank, Verb.Duct]) {
      const a = { verb: v, arg: v === Verb.Duct ? 0 : c };
      if (canDo(s, a, spent) && !out.some((o) => o.verb === a.verb && o.arg === a.arg)) out.push(a);
    }
  }
  return out;
}

export const isCarpetOnly = (g: Grid): boolean => {
  for (let c = 0; c < CELLS; c++) if (!g.wall[c] && g.floorT[c] !== Floor.Carpet) return false;
  return true;
};
